import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import ConfirmModal from '../../components/modals/ConfirmModal';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';
import { 
  getWeekNumber, 
  getWeekDateRange, 
  getCurrentWeek
} from '../../utils/weekCalculations';

const AdminExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('pending');

  // Get current week data using shared utility
  const currentWeekData = getCurrentWeek();

  // Date filter state - using From/To date range
  const [fromYear, setFromYear] = useState(currentWeekData.year);
  const [fromWeek, setFromWeek] = useState(currentWeekData.week);
  const [toYear, setToYear] = useState(currentWeekData.year);
  const [toWeek, setToWeek] = useState(currentWeekData.week);
  
  // Calendar state
  const [showFromCalendar, setShowFromCalendar] = useState(false);
  const [showToCalendar, setShowToCalendar] = useState(false);

  // Receipt modal state
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [currentReceiptUrl, setCurrentReceiptUrl] = useState(null);
  const [currentReceiptType, setCurrentReceiptType] = useState(null);

  // Bulk actions state
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [denialReason, setDenialReason] = useState('');

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });

  // Fetch expenses from API
  useEffect(() => {
    fetchExpenses();
  }, [fromYear, fromWeek, toYear, toWeek]);

  const fetchExpenses = async () => {
    setIsLoading(true);
    try {
      // Get date range for API call using shared utility
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      const toRange = getWeekDateRange(toYear, toWeek);
      const startDate = fromRange.startISO;
      const endDate = toRange.endISO;
      
      // Fetch pending expenses (always show regardless of date range)
      const pendingResponse = await axios.get(`${API}/admin/expenses`, {
        params: {
          status: 'pending'
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Fetch approved and denied expenses within date range
      const dateFilteredResponse = await axios.get(`${API}/admin/expenses`, {
        params: {
          start_date: startDate,
          end_date: endDate
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Combine results, filtering out pending from date-filtered results to avoid duplicates
      const pendingExpenses = pendingResponse.data || [];
      const dateFilteredExpenses = (dateFilteredResponse.data || []).filter(exp => exp.status !== 'pending');
      
      setExpenses([...pendingExpenses, ...dateFilteredExpenses]);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      setExpenses([]);
    }
    setIsLoading(false);
  };

  const handleStatusChange = async (id, newStatus, denialReason = '') => {
    try {
      // Update local state immediately for better UX
      setExpenses(prev => prev.map(expense => 
        expense.id === id 
          ? { ...expense, status: newStatus, denial_reason: denialReason }
          : expense
      ));

      // Make API call to update status
      await axios.put(`${API}/admin/expenses/${id}`, null, {
        params: { 
          status: newStatus,
          denial_reason: denialReason 
        },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

    } catch (error) {
      console.error('Error updating expense status:', error);
      // Revert state on error
      setExpenses(prev => prev.map(expense => 
        expense.id === id 
          ? { ...expense, status: 'pending' }
          : expense
      ));
    }
  };

  // Bulk actions functions
  const handleItemSelect = (expenseId, sectionStatus) => {
    // If selecting from a different section, clear previous selections
    if (selectedSection && selectedSection !== sectionStatus) {
      setSelectedItems([expenseId]);
      setSelectedSection(sectionStatus);
      return;
    }

    // Toggle selection within the same section
    if (selectedItems.includes(expenseId)) {
      const newSelection = selectedItems.filter(id => id !== expenseId);
      setSelectedItems(newSelection);
      if (newSelection.length === 0) {
        setSelectedSection(null);
      }
    } else {
      setSelectedItems([...selectedItems, expenseId]);
      setSelectedSection(sectionStatus);
    }
  };

  const handleSelectAll = (sectionStatus) => {
    const sectionExpenses = filteredExpenses;
    const sectionExpenseIds = sectionExpenses.map(exp => exp.id);

    // If selecting from a different section, switch to this section
    if (selectedSection !== sectionStatus) {
      setSelectedItems(sectionExpenseIds);
      setSelectedSection(sectionStatus);
      return;
    }

    // Toggle select all within the same section
    const allSelected = sectionExpenseIds.every(id => selectedItems.includes(id));
    if (allSelected) {
      // Deselect all
      setSelectedItems([]);
      setSelectedSection(null);
    } else {
      // Select all
      setSelectedItems(sectionExpenseIds);
      setSelectedSection(sectionStatus);
    }
  };

  const clearSelection = () => {
    setSelectedItems([]);
    setSelectedSection(null);
  };

  // Get available bulk actions based on selected section and payment status
  const getAvailableBulkActions = () => {
    if (!selectedSection || selectedItems.length === 0) return [];

    const selectedExpenses = expenses.filter(exp => selectedItems.includes(exp.id));
    const actions = [];

    if (selectedSection === 'pending') {
      actions.push('approve', 'deny');
    } else if (selectedSection === 'approved') {
      // Only allow deny if ALL selected expenses are unpaid
      const allUnpaid = selectedExpenses.every(exp => exp.payment_status !== 'paid');
      if (allUnpaid) {
        actions.push('deny');
      }
    } else if (selectedSection === 'denied') {
      // Allow re-approving denied expenses
      actions.push('approve');
    }

    return actions;
  };

  const handleBulkAction = async (action) => {
    if (selectedItems.length === 0) return;

    try {
      let result;
      
      if (action === 'deny') {
        // Show denial reason modal
        setShowDenialModal(true);
        return;
      } else {
        // Status update actions (approve)
        const statusMap = {
          approve: 'approved'
        };

        result = await axios.put(`${API}/admin/expenses/bulk-status`, {
          expense_ids: selectedItems,
          status: statusMap[action]
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
      }

      // Handle response for non-delete actions
      if (result.data.failure_count > 0 && result.data.success_count > 0) {
        // Partial success
        setSuccessModal({
          show: true,
          message: `${action} operation completed. ${result.data.success_count} succeeded, ${result.data.failure_count} failed.`
        });
      } else if (result.data.failure_count > 0) {
        // All failed
        setErrorModal({
          show: true,
          message: `${action} operation failed. Please try again.`
        });
      }
      // For complete success, no modal needed - UI update shows success

      // Refresh data and clear selection
      await fetchExpenses();
      clearSelection();

    } catch (error) {
      console.error(`Error in bulk ${action}:`, error);
      setErrorModal({
        show: true,
        message: `Failed to ${action} expenses. Please try again.`
      });
    }
  };

  const handleBulkDeny = async () => {
    if (!denialReason.trim()) {
      setErrorModal({
        show: true,
        message: 'Please provide a reason for denial.'
      });
      return;
    }

    try {
      const result = await axios.put(`${API}/admin/expenses/bulk-status`, {
        expense_ids: selectedItems,
        status: 'denied',
        denial_reason: denialReason
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      // Handle response
      if (result.data.failure_count > 0 && result.data.success_count > 0) {
        setSuccessModal({
          show: true,
          message: `Denial completed. ${result.data.success_count} succeeded, ${result.data.failure_count} failed.`
        });
      } else if (result.data.failure_count > 0) {
        setErrorModal({
          show: true,
          message: 'Denial operation failed. Please try again.'
        });
      }

      // Refresh data and clear selection
      await fetchExpenses();
      clearSelection();
      setShowDenialModal(false);
      setDenialReason('');

    } catch (error) {
      console.error('Error in bulk deny:', error);
      setErrorModal({
        show: true,
        message: 'Failed to deny expenses. Please try again.'
      });
    }
  };

  // Format week display using shared utility
  function formatWeekDisplay(year, weekNum) {
    const { startStr, endStr } = getWeekDateRange(year, weekNum);
    return `${year} Week ${weekNum} (${startStr} - ${endStr})`;
  }

  // Handle date click in calendar - matching Team Performance page logic
  function handleDateClick(date, isFrom = true) {
    const year = date.getFullYear();
    const weekNum = getWeekNumber(date, year);
    
    if (isFrom) {
      setFromYear(year);
      setFromWeek(weekNum);
      setShowFromCalendar(false);
      
      const fromRange = getWeekDateRange(year, weekNum);
      const toRange = getWeekDateRange(toYear, toWeek);
      
      if (fromRange.start > toRange.start) {
        setToYear(year);
        setToWeek(weekNum);
      }
    } else {
      const toRange = getWeekDateRange(year, weekNum);
      const fromRange = getWeekDateRange(fromYear, fromWeek);
      
      if (toRange.start < fromRange.start) {
        setFromYear(year);
        setFromWeek(weekNum);
      }
      
      setToYear(year);
      setToWeek(weekNum);
      setShowToCalendar(false);
    }
  }

  // Generate calendar for a specific month - matching Team Performance page
  function generateCalendar(currentDate) {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < 42; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    return { weeks, month, year };
  }

  // Go to current week
  const goToCurrentWeek = () => {
    const { year, week } = getCurrentWeek();
    setFromYear(year);
    setFromWeek(week);
    setToYear(year);
    setToWeek(week);
  };

  // Receipt Modal Component - Dark Theme
  const ReceiptModal = () => {
    if (!showReceiptModal || !currentReceiptUrl) return null;

    const closeModal = () => {
      setShowReceiptModal(false);
      setCurrentReceiptUrl(null);
      setCurrentReceiptType(null);
    };

    const renderReceiptContent = () => {
      switch (currentReceiptType) {
        case 'image':
          return (
            <img
              src={currentReceiptUrl}
              alt="Receipt"
              className="max-w-full max-h-full object-contain"
              style={{ maxHeight: '80vh' }}
            />
          );
        case 'pdf':
          return (
            <iframe
              src={currentReceiptUrl}
              title="Receipt PDF"
              className="w-full border-0"
              style={{ height: '80vh' }}
            />
          );
        case 'data':
        case 'link':
        default:
          return (
            <div className="flex flex-col items-center space-y-4">
              <div className="w-full max-h-96 overflow-auto border border-slate-600 rounded-lg">
                {currentReceiptType === 'data' ? (
                  currentReceiptUrl.includes('image') ? (
                    <img
                      src={currentReceiptUrl}
                      alt="Receipt"
                      className="max-w-full h-auto object-contain"
                    />
                  ) : currentReceiptUrl.includes('pdf') ? (
                    <iframe
                      src={currentReceiptUrl}
                      title="Receipt PDF"
                      className="w-full h-96 border-0"
                    />
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-gray-400 mb-4">Preview not available</p>
                      <a
                        href={currentReceiptUrl}
                        download
                        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-500 transition-colors"
                      >
                        Download Receipt
                      </a>
                    </div>
                  )
                ) : (
                  <iframe
                    src={currentReceiptUrl}
                    title="Receipt"
                    className="w-full h-96 border-0"
                  />
                )}
              </div>
            </div>
          );
      }
    };

    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={closeModal}>
        <div className="bg-slate-900 rounded-2xl shadow-2xl max-w-4xl max-h-screen overflow-hidden border border-slate-700" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 px-6 py-4 flex items-center justify-between">
            <h3 className="text-xl font-semibold text-white">Receipt Preview</h3>
            <button
              onClick={closeModal}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Modal Content */}
          <div className="p-6 flex justify-center items-center bg-slate-800">
            {renderReceiptContent()}
          </div>
          
          {/* Modal Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-slate-700 bg-slate-900">
            <button
              onClick={handleDownloadReceipt}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-500 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Download</span>
            </button>
            <a
              href={currentReceiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-500 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              <span>Open in New Tab</span>
            </a>
            <button
              onClick={closeModal}
              className="bg-slate-700 text-white px-4 py-2 rounded-lg hover:bg-slate-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const filteredExpenses = expenses.filter(expense => expense.status === selectedTab);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  // Get the appropriate date for display based on expense status
  const getDisplayDate = (expense) => {
    if ((expense.status === 'approved' || expense.status === 'denied') && expense.approval_date) {
      return expense.approval_date;
    }
    return expense.date;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-300 border border-green-500/30">Approved</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-500/20 text-yellow-300 border border-yellow-500/30">Pending</span>;
      case 'denied':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-500/20 text-red-300 border border-red-500/30">Denied</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-slate-600 text-gray-300">Unknown</span>;
    }
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">Paid</span>;
      case 'unpaid':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">Unpaid</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">Unpaid</span>;
    }
  };

  const handleReceiptView = (receiptUrl) => {
    if (!receiptUrl) return;
    
    let receiptType = 'unknown';
    if (receiptUrl.includes('data:image/')) {
      receiptType = 'image';
    } else if (receiptUrl.includes('data:application/pdf')) {
      receiptType = 'pdf';
    } else if (receiptUrl.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i)) {
      receiptType = 'image';
    } else if (receiptUrl.match(/\.(pdf)(\?|$)/i)) {
      receiptType = 'pdf';
    } else if (receiptUrl.startsWith('data:')) {
      receiptType = 'data';
    } else {
      receiptType = 'link';
    }
    
    setCurrentReceiptUrl(receiptUrl);
    setCurrentReceiptType(receiptType);
    setShowReceiptModal(true);
  };

  const handleDownloadReceipt = () => {
    if (!currentReceiptUrl) return;

    try {
      const getFileName = () => {
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        
        if (currentReceiptType === 'image' || currentReceiptUrl.includes('data:image/')) {
          if (currentReceiptUrl.includes('jpeg') || currentReceiptUrl.includes('jpg')) {
            return `receipt_${timestamp}.jpg`;
          } else if (currentReceiptUrl.includes('png')) {
            return `receipt_${timestamp}.png`;
          } else if (currentReceiptUrl.includes('gif')) {
            return `receipt_${timestamp}.gif`;
          } else if (currentReceiptUrl.includes('webp')) {
            return `receipt_${timestamp}.webp`;
          } else {
            return `receipt_${timestamp}.jpg`;
          }
        } else if (currentReceiptType === 'pdf' || currentReceiptUrl.includes('application/pdf')) {
          return `receipt_${timestamp}.pdf`;
        } else {
          const urlParts = currentReceiptUrl.split('.');
          const extension = urlParts.length > 1 ? urlParts[urlParts.length - 1].split('?')[0] : 'file';
          return `receipt_${timestamp}.${extension}`;
        }
      };

      const fileName = getFileName();

      if (currentReceiptUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = currentReceiptUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        fetch(currentReceiptUrl)
          .then(response => response.blob())
          .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(error => {
            console.error('Error downloading receipt:', error);
            window.open(currentReceiptUrl, '_blank');
          });
      }
    } catch (error) {
      console.error('Error downloading receipt:', error);
      window.open(currentReceiptUrl, '_blank');
    }
  };

  const pendingCount = expenses.filter(exp => exp.status === 'pending').length;
  const approvedCount = expenses.filter(exp => exp.status === 'approved').length;
  const deniedCount = expenses.filter(exp => exp.status === 'denied').length;

  // Calendar Component - Dark Theme matching Team Performance page
  const Calendar = ({ onDateClick, onClose, selectedYear, selectedWeek, isFromCalendar }) => {
    // Initialize display date based on the selected week (not current date)
    const initialDisplayDate = () => {
      const weekRange = getWeekDateRange(selectedYear, selectedWeek);
      return weekRange.start;
    };
    const [displayDate, setDisplayDate] = useState(initialDisplayDate);
    const { weeks, month, year } = generateCalendar(displayDate);
    const calendarRef = useRef(null);
    
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
          onClose();
        }
      };
      
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const navigateMonth = (direction) => {
      const newDate = new Date(displayDate);
      newDate.setMonth(newDate.getMonth() + direction);
      
      const today = new Date();
      const maxDate = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      
      if (direction > 0 && newDate > maxDate) {
        return;
      }
      
      setDisplayDate(newDate);
    };
    
    const isSelectedWeek = (date) => {
      const year = date.getFullYear();
      const weekNum = getWeekNumber(date, year);
      return year === selectedYear && weekNum === selectedWeek;
    };
    
    return (
      <div className="relative">
        <div className={`absolute z-[9999] mt-2 ${isFromCalendar ? 'left-0' : 'right-0'}`}>
          <div 
            ref={calendarRef}
            className="bg-slate-800 rounded-xl shadow-xl border border-slate-600 p-6 w-80"
          >
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth(-1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h3 className="text-lg font-semibold text-white">
                {monthNames[month]} {year}
              </h3>
              <button
                onClick={() => navigateMonth(1)}
                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
                <div key={index} className="text-center text-xs font-semibold text-gray-400 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="space-y-1">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="grid grid-cols-7 gap-1">
                  {week.map((date, dateIndex) => {
                    const isCurrentMonth = date.getMonth() === month;
                    const isToday = date.toDateString() === new Date().toDateString();
                    const isSelected = isSelectedWeek(date);
                    const isFutureDate = date > new Date();
                    
                    return (
                      <button
                        key={dateIndex}
                        onClick={() => !isFutureDate && onDateClick(date)}
                        disabled={isFutureDate}
                        className={`
                          p-2 text-sm rounded-lg transition-all duration-200 hover:scale-105
                          ${isFutureDate 
                            ? 'text-gray-600 cursor-not-allowed opacity-50' 
                            : isCurrentMonth 
                              ? 'text-white hover:bg-slate-700' 
                              : 'text-gray-500'
                          }
                          ${isSelected ? 'bg-purple-600 text-white hover:bg-purple-700' : ''}
                          ${isToday && !isSelected ? 'ring-2 ring-purple-400' : ''}
                        `}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold mb-1">Expenses</h1>
        <p className="text-purple-200">Review and approve expense claims</p>
      </div>

      {/* Filters Section - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6 overflow-visible">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-white ml-3">Filters & Date Range</h2>
          </div>
          
          <button
            onClick={goToCurrentWeek}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 text-sm flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
            </svg>
            Current Week
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* From Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              From Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowFromCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(fromYear, fromWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showFromCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, true)}
                  onClose={() => setShowFromCalendar(false)}
                  selectedYear={fromYear}
                  selectedWeek={fromWeek}
                  isFromCalendar={true}
                />
              )}
            </div>
          </div>

          {/* To Filter */}
          <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600 relative">
            <h4 className="text-xs font-bold text-gray-300 mb-2 flex items-center">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
              </svg>
              To Date
            </h4>
            <div className="relative">
              <button
                onClick={() => setShowToCalendar(true)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-left flex items-center justify-between hover:bg-slate-700"
              >
                <span className="truncate">{formatWeekDisplay(toYear, toWeek)}</span>
                <svg className="w-4 h-4 text-gray-400 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                </svg>
              </button>
              
              {showToCalendar && (
                <Calendar
                  onDateClick={(date) => handleDateClick(date, false)}
                  onClose={() => setShowToCalendar(false)}
                  selectedYear={toYear}
                  selectedWeek={toWeek}
                  isFromCalendar={false}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Section - Dark Theme */}
      {selectedItems.length > 0 && (
        <div className="mb-6 bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center mr-3">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="text-lg font-semibold text-white">
                Bulk Actions ({selectedItems.length} items selected)
              </div>
              <div className="ml-2 text-sm text-purple-400 font-medium capitalize">
                from {selectedSection} expenses
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getAvailableBulkActions().includes('approve') && (
                <button
                  onClick={() => handleBulkAction('approve')}
                  className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve
                </button>
              )}
              {getAvailableBulkActions().includes('deny') && (
                <button
                  onClick={() => handleBulkAction('deny')}
                  className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg transition-all duration-200 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Deny
                </button>
              )}
              <button
                onClick={clearSelection}
                className="text-gray-300 hover:text-white px-3 py-2 rounded-lg border border-slate-600 hover:bg-slate-700 transition-colors"
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        <div className="border-b border-slate-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {['pending', 'approved', 'denied'].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setSelectedTab(tab);
                  clearSelection();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-slate-600'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  tab === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                  tab === 'approved' ? 'bg-green-500/20 text-green-300' :
                  'bg-red-500/20 text-red-300'
                }`}>
                  {tab === 'pending' ? pendingCount : tab === 'approved' ? approvedCount : deniedCount}
                </span>
              </button>
            ))}
          </nav>
        </div>

        {/* Expenses Table - Dark Theme */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            <span className="ml-3 text-gray-400">Loading expenses...</span>
          </div>
        ) : filteredExpenses.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No {selectedTab} expenses</h3>
            <p className="text-gray-400">There are no {selectedTab} expense claims at the moment.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={filteredExpenses.length > 0 && filteredExpenses.every(exp => selectedItems.includes(exp.id))}
                      ref={input => {
                        if (input) {
                          const someSelected = selectedItems.length > 0 && !filteredExpenses.every(exp => selectedItems.includes(exp.id));
                          input.indeterminate = someSelected;
                        }
                      }}
                      onChange={() => handleSelectAll(selectedTab)}
                      className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Receipt</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                  {selectedTab === 'approved' && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Payment Status</th>
                  )}
                  {selectedTab === 'pending' && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense, index) => (
                  <tr 
                    key={expense.id} 
                    className={`hover:bg-slate-700/50 transition-colors ${selectedItems.includes(expense.id) ? 'bg-purple-500/10' : ''} ${index !== filteredExpenses.length - 1 ? 'border-b border-slate-700/50' : ''}`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(expense.id)}
                        onChange={() => handleItemSelect(expense.id, selectedTab)}
                        className="w-4 h-4 text-purple-600 bg-slate-700 border-slate-500 rounded focus:ring-purple-500"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {expense.employee_name?.charAt(0).toUpperCase() || expense.user?.first_name?.charAt(0).toUpperCase() || expense.user_name?.charAt(0).toUpperCase() || 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {expense.employee_name || `${expense.user?.first_name || ''} ${expense.user?.last_name || ''}`.trim() || expense.user_name || 'Unknown Employee'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300">{expense.description}</div>
                      {expense.denial_reason && (
                        <div className="text-xs text-red-400 mt-1">
                          Reason: {expense.denial_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-slate-700 text-gray-300">
                        {expense.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(getDisplayDate(expense))}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {expense.receipt_url ? (
                        <button
                          onClick={() => handleReceiptView(expense.receipt_url)}
                          className="text-purple-400 hover:text-purple-300 underline"
                        >
                          View Receipt
                        </button>
                      ) : (
                        <span className="text-gray-500">No receipt</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(expense.status)}</td>
                    {selectedTab === 'approved' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(expense.payment_status)}
                      </td>
                    )}
                    {selectedTab === 'pending' && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleStatusChange(expense.id, 'approved')}
                          className="text-green-400 hover:text-green-300 px-3 py-1 rounded bg-green-500/10 hover:bg-green-500/20 border border-green-500/30"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedItems([expense.id]);
                            setSelectedSection('pending');
                            setShowDenialModal(true);
                          }}
                          className="text-red-400 hover:text-red-300 px-3 py-1 rounded bg-red-500/10 hover:bg-red-500/20 border border-red-500/30"
                        >
                          Deny
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bulk Denial Modal - Dark Theme */}
      {showDenialModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 border border-slate-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="text-xl font-bold text-white">Deny Expenses</h3>
              <button
                onClick={() => {
                  setShowDenialModal(false);
                  setDenialReason('');
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-2 transition-all duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-gray-300 mb-2">
                  Deny <span className="font-semibold text-red-400">{selectedItems.length} selected expense(s)</span>
                </p>
                <p className="text-sm text-gray-400">Please provide a reason for the denial:</p>
              </div>

              <div className="space-y-3">
                <textarea
                  value={denialReason}
                  onChange={(e) => setDenialReason(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  placeholder="Enter reason for denial..."
                />
              </div>

              {/* Modal Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-700 mt-6">
                <button
                  onClick={() => {
                    setShowDenialModal(false);
                    setDenialReason('');
                  }}
                  className="px-6 py-2 text-gray-300 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkDeny}
                  disabled={!denialReason.trim()}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    denialReason.trim()
                      ? 'bg-red-600 text-white hover:bg-red-500'
                      : 'bg-slate-700 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Deny Expenses
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      <ReceiptModal />

      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default AdminExpensesPage;
